/**
 * Camera perspective solver — pure math module.
 *
 * Given vanishing lines drawn on a reference image, computes camera focal
 * length, rotation, and position under PoseRef's no-roll (gravity-aligned)
 * camera model.
 *
 * ## Algorithm
 *
 * 1. Group user-labeled lines by axis, build normalized homogeneous forms.
 * 2. Compute initial vanishing points for the X and Y axes via the shared
 *    weighted-LS `svdVanishingPoint` kernel — strict improvement over the
 *    legacy median-of-pairwise-intersections approach for any axis with
 *    ≥4 lines, identical for 2 lines.
 * 3. Derive an initial (pitch, yaw, focalPx) from the simplified no-roll
 *    orthogonality formula `f² = −dX·dY − h²`.
 * 4. Refine (pitch, yaw, focalPx) via the shared 3-DOF Manhattan
 *    Levenberg–Marquardt with Tukey biweight loss. Same kernel as the
 *    auto-align worker — guarantees consistent behavior between automatic
 *    detection and live solve as the user edits lines.
 * 5. Build R = Rx(pitch) · R_base · Rz(yaw); the no-roll constraint is
 *    encoded in the parameterization, so the rotation matrix is a single
 *    deterministic element of SO(3) and the prior 4-way sign brute-force
 *    is unnecessary. The only remaining ambiguity is the (yaw, yaw + π)
 *    "looking forward vs backward" pair, resolved by the optional
 *    `currentFwd` hint.
 * 6. Convert focal length to mm and surface a warning if it lands outside
 *    the typical [15, 200] mm range — the original code clamped silently,
 *    which masked bad line placement.
 *
 * ## Coordinate conventions
 *
 * - **PoseRef world space**: X = right, Y = forward, Z = up.
 * - **Camera frame**: X = right, Y = up, Z = backward (looks along −Z).
 * - **Image space**: origin at top-left, X = right, Y = down.
 *   Vanishing lines are stored in normalized [0,1]×[0,1] coords.
 * - **Sensor height**: 24 mm (Blender standard).
 *
 * ## No-roll requirement
 *
 * The solver requires line classifications on **both** the X and Y world
 * axes. Z-axis lines (vertical world rulings) are accepted as additional
 * constraint to the LM refinement but are not sufficient on their own —
 * under the no-roll parameterization, the world-Z VP is identically at
 * column `cx` for any (pitch, yaw), so vertical image lines provide no
 * information that the parameterization doesn't already encode.
 */

import type { ReferenceDistance, VanishingLine } from "../app/storeTypes.ts";
import { type RefineLineInput, refineManhattan } from "./perspectiveRefine.ts";
import {
  homogeneousLine,
  type Mat3,
  mat3MulVec3,
  mat3Transpose,
  rotationFromPitchYaw,
  svdVanishingPoint,
  type Vec2,
} from "./vpMath.ts";

const SENSOR_HEIGHT = 24; // mm
const FOCAL_MM_MIN = 15;
const FOCAL_MM_MAX = 200;

type V3 = [number, number, number];

export interface CameraSolution {
  focalLengthMm: number;
  position: [number, number, number];
  target: [number, number, number];
  /**
   * Optional non-blocking notice about the solve. Currently set when the
   * computed focal length falls outside the typical [15, 200] mm range and
   * has been clamped — surfaces "your lines might be off" without erroring.
   */
  warning?: string;
}

// ---------------------------------------------------------------------------
// Reference-distance backprojection
// ---------------------------------------------------------------------------

/**
 * Backproject a normalized image point to the ground plane `Z = groundZ`.
 *
 * Returns the world-space [X, Y, groundZ] intersection or `null` if the ray
 * doesn't intersect the plane in front of the camera (camera below ground
 * looking down, or looking parallel to the ground).
 */
function backprojectToGround(
  nx: number,
  ny: number,
  imgWidth: number,
  imgHeight: number,
  focalPx: number,
  R: Mat3,
  cameraPos: V3,
  groundZ: number,
): V3 | null {
  const cx = imgWidth / 2;
  const cy = imgHeight / 2;
  const px = nx * imgWidth;
  const py = ny * imgHeight;
  // Camera-space ray direction (camera looks along -Z).
  // From pinhole: image_x = cx − f·X/Z, image_y = cy + f·Y/Z
  // Invert with Z = −f: X = px − cx, Y = cy − py
  const dc: [number, number, number] = [px - cx, cy - py, -focalPx];
  // World-space ray direction
  const Rt = mat3Transpose(R);
  const dw = mat3MulVec3(Rt, dc);
  if (Math.abs(dw[2]) < 1e-12) return null;
  const t = (groundZ - cameraPos[2]) / dw[2];
  if (t < 0) return null;
  return [cameraPos[0] + t * dw[0], cameraPos[1] + t * dw[1], groundZ];
}

/**
 * Compute the orbit-distance scale factor that makes a reference distance
 * match a known world-space length on the ground plane.
 *
 * Returns the scale `s` such that `orbitDist * s` places the camera at the
 * correct distance, or `null` if the backprojection is degenerate.
 */
function referenceDistanceScale(
  ref: { p1: [number, number]; p2: [number, number]; length: number },
  imgWidth: number,
  imgHeight: number,
  focalPx: number,
  R: Mat3,
  cameraPos: V3,
  groundZ: number,
): number | null {
  const a = backprojectToGround(
    ref.p1[0],
    ref.p1[1],
    imgWidth,
    imgHeight,
    focalPx,
    R,
    cameraPos,
    groundZ,
  );
  const b = backprojectToGround(
    ref.p2[0],
    ref.p2[1],
    imgWidth,
    imgHeight,
    focalPx,
    R,
    cameraPos,
    groundZ,
  );
  if (!a || !b) return null;
  const d = Math.hypot(a[0] - b[0], a[1] - b[1]);
  if (d < 1e-12 || ref.length <= 0) return null;
  return ref.length / d;
}

/**
 * Solve camera parameters from vanishing lines under the no-roll model.
 *
 * Requires at least 2 lines on each of the X and Y world axes. Z lines are
 * optional and only contribute to LM refinement.
 *
 * @param lines      All vanishing lines for this reference image.
 * @param imgWidth   Natural image width in pixels.
 * @param imgHeight  Natural image height in pixels.
 * @param groundZ    World-space Z of the ground plane.
 * @param orbitDist  Working orbit distance — preserves the user's framing.
 * @param currentFwd Current camera forward direction. Resolves the
 *                   (yaw, yaw + π) "facing forward vs backward" ambiguity
 *                   by picking whichever forward is closer to the hint.
 *                   Without it, the solver defaults to a forward-facing
 *                   solution biased by the initial-guess yaw range.
 * @param refDist    Optional known ground-plane distance for camera distance
 *                   recovery. Two image-space handles + their world length.
 */
export function solveCamera(
  lines: VanishingLine[],
  imgWidth: number,
  imgHeight: number,
  groundZ: number,
  orbitDist: number,
  currentFwd?: V3,
  refDist?: ReferenceDistance,
): CameraSolution | { error: string } | null {
  // -----------------------------------------------------------------------
  // 1. Group lines by axis, lift to pixel coords, build homogeneous forms
  // -----------------------------------------------------------------------

  const xLines: { line: [number, number, number]; weight: number }[] = [];
  const yLines: { line: [number, number, number]; weight: number }[] = [];
  const zLines: { line: [number, number, number]; weight: number }[] = [];

  for (const l of lines) {
    const start: Vec2 = [l.start[0] * imgWidth, l.start[1] * imgHeight];
    const end: Vec2 = [l.end[0] * imgWidth, l.end[1] * imgHeight];
    const homo = homogeneousLine(start, end);
    const weight = Math.hypot(end[0] - start[0], end[1] - start[1]);
    if (weight < 1e-6) continue;
    const wl = { line: homo, weight };
    if (l.axis === "x") xLines.push(wl);
    else if (l.axis === "y") yLines.push(wl);
    else zLines.push(wl);
  }

  if (xLines.length < 2 || yLines.length < 2) {
    return { error: "Need at least 2 lines on each of the X and Y axes" };
  }

  // -----------------------------------------------------------------------
  // 2. Initial vanishing points via weighted least-squares
  // -----------------------------------------------------------------------

  const vpX = svdVanishingPoint(xLines);
  const vpY = svdVanishingPoint(yLines);

  if (!vpX || !vpY || !Number.isFinite(vpX[0]) || !Number.isFinite(vpY[0])) {
    return { error: "X or Y lines are parallel — no finite vanishing point" };
  }

  // -----------------------------------------------------------------------
  // 3. Initial guess (pitch, yaw, focalPx) from no-roll orthogonality
  //
  //    Under the no-roll constraint both horizon VPs share the same y, so:
  //
  //      h     = yh − cy        (signed perpendicular from PP to horizon)
  //      f²    = −dX · dY − h²  (orthogonality with the h² correction)
  //      pitch = atan2(−h, f)
  //      tan(yaw) · cos(pitch) = f / dX        (from world-X projection)
  //      ⇒ yaw = atan2(f, dX · cos(pitch))
  // -----------------------------------------------------------------------

  const cx = imgWidth / 2;
  const cy = imgHeight / 2;

  // Average the two horizon-row estimates — both should agree exactly under
  // the LS fit but small numerical drift is normal with few lines.
  const yh = (vpX[1] + vpY[1]) / 2;
  const dX = vpX[0] - cx;
  const dY = vpY[0] - cx;
  const h = yh - cy;

  const fSqr = -dX * dY - h * h;
  if (fSqr <= 0) {
    return {
      error:
        "Lines don't form orthogonal vanishing points — check line placement",
    };
  }
  const focalInit = Math.sqrt(fSqr);
  const pitchInit = Math.atan2(-h, focalInit);
  const cpInit = Math.cos(pitchInit);
  const yawInit = Math.atan2(focalInit, dX * cpInit);

  // -----------------------------------------------------------------------
  // 4. LM refinement against the full labeled line set
  // -----------------------------------------------------------------------

  const refineLines: RefineLineInput[] = [];
  for (const wl of xLines) refineLines.push({ ...wl, axis: "x" });
  for (const wl of yLines) refineLines.push({ ...wl, axis: "y" });
  for (const wl of zLines) refineLines.push({ ...wl, axis: "z" });

  const refined = refineManhattan({
    lines: refineLines,
    imgWidth,
    imgHeight,
    initial: { pitch: pitchInit, yaw: yawInit, focalPx: focalInit },
  });

  // -----------------------------------------------------------------------
  // 5. Reconstruct camera rotation, resolve forward/backward ambiguity
  //
  // The horizon VPs are invariant under (yaw → yaw + π) — i.e., looking
  // forward and looking backward produce the same image. LM converges to
  // one of the two solutions based on the initial guess; if `currentFwd`
  // is provided, pick whichever recovered forward agrees with it.
  // -----------------------------------------------------------------------

  const R1 = rotationFromPitchYaw(refined.pitch, refined.yaw);
  // forward_world = R^T · (0, 0, −1) = -third row of R.
  const fwd1: V3 = [-R1[6], -R1[7], -R1[8]];

  let chosenFwd = fwd1;
  if (currentFwd) {
    const R2 = rotationFromPitchYaw(refined.pitch, refined.yaw + Math.PI);
    const fwd2: V3 = [-R2[6], -R2[7], -R2[8]];
    const dot1 =
      fwd1[0] * currentFwd[0] +
      fwd1[1] * currentFwd[1] +
      fwd1[2] * currentFwd[2];
    const dot2 =
      fwd2[0] * currentFwd[0] +
      fwd2[1] * currentFwd[1] +
      fwd2[2] * currentFwd[2];
    if (dot2 > dot1) chosenFwd = fwd2;
  }

  // -----------------------------------------------------------------------
  // 6. Position and target for OrbitControls
  //
  // If a reference distance is provided, compute the scale factor that
  // makes the backprojected image-space handles span the user's known
  // world-space length on the ground plane. Otherwise preserve orbitDist.
  // -----------------------------------------------------------------------

  let dist = Math.max(0.5, orbitDist);
  const target: V3 = [0, 0, groundZ];

  const chosenR =
    chosenFwd === fwd1
      ? R1
      : rotationFromPitchYaw(refined.pitch, refined.yaw + Math.PI);

  if (refDist) {
    const tentativePos: V3 = [
      target[0] - chosenFwd[0] * dist,
      target[1] - chosenFwd[1] * dist,
      target[2] - chosenFwd[2] * dist,
    ];
    const s = referenceDistanceScale(
      refDist,
      imgWidth,
      imgHeight,
      refined.focalPx,
      chosenR,
      tentativePos,
      groundZ,
    );
    if (s !== null && Number.isFinite(s) && s > 0) {
      dist = dist * s;
    }
  }

  const position: V3 = [
    target[0] - chosenFwd[0] * dist,
    target[1] - chosenFwd[1] * dist,
    target[2] - chosenFwd[2] * dist,
  ];

  // -----------------------------------------------------------------------
  // 7. Focal length in mm
  // -----------------------------------------------------------------------

  const focalPx = refined.focalPx;
  const isLandscape = imgWidth > imgHeight;
  const focalAngle = isLandscape
    ? 2 * Math.atan(imgHeight / 2 / focalPx)
    : 2 * Math.atan(imgWidth / 2 / focalPx);

  const sensorHeight = isLandscape
    ? SENSOR_HEIGHT
    : SENSOR_HEIGHT * (imgWidth / imgHeight);
  const focalLengthMmRaw = sensorHeight / (2 * Math.tan(focalAngle / 2));
  const focalLengthMm = Math.max(
    FOCAL_MM_MIN,
    Math.min(FOCAL_MM_MAX, focalLengthMmRaw),
  );

  const warning =
    focalLengthMm !== focalLengthMmRaw
      ? `Computed focal length ${focalLengthMmRaw.toFixed(0)} mm clamped to ${focalLengthMm.toFixed(0)} mm — check line placement`
      : undefined;

  return { focalLengthMm, position, target, warning };
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

/**
 * Check if there are enough lines to attempt a solve. Under the no-roll
 * model both X and Y axes must have at least 2 lines each — Z is optional.
 */
export function canSolve(lines: VanishingLine[]): boolean {
  let xCount = 0;
  let yCount = 0;
  for (const l of lines) {
    if (l.axis === "x") xCount++;
    else if (l.axis === "y") yCount++;
  }
  return xCount >= 2 && yCount >= 2;
}
